import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

interface Notificacao {
  id: string;
  titulo: string;
  mensagem: string;
  tipo: 'info' | 'sucesso' | 'aviso' | 'erro';
  duracao?: number; // em milissegundos
}

interface NotificacaoContextProps {
  notificacoes: Notificacao[];
  adicionarNotificacao: (notificacao: Omit<Notificacao, 'id'>) => void;
  removerNotificacao: (id: string) => void;
}

const NotificacaoContext = createContext<NotificacaoContextProps | undefined>(undefined);

export const useNotificacao = () => {
  const context = useContext(NotificacaoContext);
  if (!context) {
    throw new Error('useNotificacao deve ser usado dentro de um NotificacaoProvider');
  }
  return context;
};

interface NotificacaoProviderProps {
  children: ReactNode;
}

export const NotificacaoProvider: React.FC<NotificacaoProviderProps> = ({ children }) => {
  const [notificacoes, setNotificacoes] = useState<Notificacao[]>([]);

  // Função para adicionar uma nova notificação
  const adicionarNotificacao = (notificacao: Omit<Notificacao, 'id'>) => {
    const id = Math.random().toString(36).substring(2, 9);
    const novaNotificacao = {
      ...notificacao,
      id,
      duracao: notificacao.duracao || 5000, // Padrão de 5 segundos se não especificado
    };
    
    setNotificacoes((prev) => [...prev, novaNotificacao]);
    
    // Solicitar permissão para notificações do navegador
    if (Notification.permission === 'granted') {
      // Criar notificação do navegador
      new Notification(notificacao.titulo, {
        body: notificacao.mensagem,
        icon: '/favicon.png'
      });
    } else if (Notification.permission !== 'denied') {
      Notification.requestPermission().then(permission => {
        if (permission === 'granted') {
          new Notification(notificacao.titulo, {
            body: notificacao.mensagem,
            icon: '/favicon.png'
          });
        }
      });
    }
    
    // Remover automaticamente após o tempo definido
    setTimeout(() => {
      removerNotificacao(id);
    }, novaNotificacao.duracao);
  };

  // Função para remover uma notificação
  const removerNotificacao = (id: string) => {
    setNotificacoes((prev) => prev.filter((notificacao) => notificacao.id !== id));
  };

  // Solicitar permissão para notificações quando o componente for montado
  useEffect(() => {
    if (Notification.permission !== 'granted' && Notification.permission !== 'denied') {
      Notification.requestPermission();
    }
  }, []);

  return (
    <NotificacaoContext.Provider
      value={{
        notificacoes,
        adicionarNotificacao,
        removerNotificacao,
      }}
    >
      {children}
    </NotificacaoContext.Provider>
  );
};
